/**
 * ProductAttributesCard — dynamic attribute form per TikTok product category.
 *
 * Fetches attributes for the current category and renders form fields.
 * Only shows PRODUCT_ATTRIBUTE type (not SALES_ATTRIBUTE for variants).
 * Required attributes are validated — errors block save via the store.
 *
 * Hidden entirely when category has no product attributes.
 */

import { useEffect, useMemo, useCallback, useState, useRef } from "react";
import { useQuery, keepPreviousData } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import type { ExperienceWidgetProps } from "@/lib/views/widgets/registry";
import {
  tiktokProductApi,
  type TikTokAttribute,
} from "../api/tiktokProductApi";
import {
  useTikTokEditStore,
  selectFieldValue,
} from "../stores/useTikTokEditStore";
import { Divider } from "./shared";

// ── Types ──────────────────────────────────────────────────────────

interface AttributeValue {
  id: string;
  values: Array<{ id?: string; name?: string }>;
}

// ── Component ──────────────────────────────────────────────────────

export function ProductAttributesCard({ section }: ExperienceWidgetProps) {
  const channel = section.description || "";
  if (channel !== "TikTok Shop") return null;

  return <TikTokAttributesCard />;
}

function TikTokAttributesCard() {
  const connectionName = useTikTokEditStore((s) => s.connectionName);
  const categoryId = useTikTokEditStore(selectFieldValue("category_id")) as string;
  const currentAttrs = useTikTokEditStore(selectFieldValue("product_attributes")) as AttributeValue[] | undefined;
  const setField = useTikTokEditStore((s) => s.setField);
  const setValidationError = useTikTokEditStore((s) => s.setValidationError);
  const clearValidationError = useTikTokEditStore((s) => s.clearValidationError);
  const clearAllValidationErrors = useTikTokEditStore((s) => s.clearAllValidationErrors);
  const validationErrors = useTikTokEditStore((s) => s.validationErrors);

  const { data: allAttributes, isLoading } = useQuery({
    queryKey: ["tiktok", "category-attributes", connectionName, categoryId],
    queryFn: () => tiktokProductApi.getCategoryAttributes(connectionName, categoryId),
    enabled: !!categoryId && !!connectionName,
    staleTime: 5 * 60 * 1000,
    placeholderData: keepPreviousData,
  });

  // Filter to PRODUCT_ATTRIBUTE only
  const productAttributes = useMemo(
    () => allAttributes?.filter((a) => a.type === "PRODUCT_ATTRIBUTE") ?? [],
    [allAttributes],
  );

  // Merge existing values with new schema when attributes load
  const mergedValues = useMemo(() => {
    if (productAttributes.length === 0) return [];
    const existing = currentAttrs ?? [];
    return productAttributes.map((attr) => {
      const match = existing.find((v) => v.id === attr.id);
      return match || { id: attr.id, values: [] };
    });
  }, [productAttributes, currentAttrs]);

  // Validate required attributes whenever values change
  useEffect(() => {
    for (const attr of productAttributes) {
      const key = `attr_${attr.id}`;
      const val = mergedValues.find((v) => v.id === attr.id);
      if (attr.is_required && (!val || val.values.length === 0)) {
        setValidationError(key, `${attr.name} is required`);
      } else {
        clearValidationError(key);
      }
    }
  }, [mergedValues, productAttributes, setValidationError, clearValidationError]);

  // Cleanup validation errors on unmount
  useEffect(() => {
    return () => {
      clearAllValidationErrors();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleAttributeChange = useCallback(
    (attrId: string, newValue: { id?: string; name?: string } | null) => {
      const updated = mergedValues.map((v) => {
        if (v.id !== attrId) return v;
        return {
          id: attrId,
          values: newValue ? [newValue] : [],
        };
      });
      setField("product_attributes", updated);
    },
    [mergedValues, setField],
  );

  // Don't render if no category or no product attributes
  if (!categoryId || (!isLoading && productAttributes.length === 0)) {
    return null;
  }

  if (isLoading) {
    return (
      <div className="space-y-2">
        <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide pl-4 mb-2">
          Product Attributes
        </h3>
        <div className="rounded-2xl overflow-hidden bg-card flex items-center justify-center py-8">
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide pl-4 mb-2">
        Product Attributes
      </h3>
      <div className="rounded-2xl overflow-hidden bg-card">
        {productAttributes.map((attr, i) => {
          const value = mergedValues.find((v) => v.id === attr.id);
          const errorKey = `attr_${attr.id}`;
          const error = validationErrors[errorKey];

          return (
            <div key={attr.id}>
              {i > 0 && <Divider />}
              <AttributeField
                attribute={attr}
                value={value}
                error={error}
                onChange={(val) => handleAttributeChange(attr.id, val)}
              />
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── AttributeField ─────────────────────────────────────────────────

function AttributeField({
  attribute,
  value,
  error,
  onChange,
}: {
  attribute: TikTokAttribute;
  value: AttributeValue | undefined;
  error: string | undefined;
  onChange: (val: { id?: string; name?: string } | null) => void;
}) {
  const selectedValue = value?.values?.[0];
  const hasValues = attribute.values && attribute.values.length > 0;

  if (hasValues && attribute.is_customizable) {
    return (
      <ComboboxField
        attribute={attribute}
        selectedValue={selectedValue}
        error={error}
        onChange={onChange}
      />
    );
  }

  if (hasValues) {
    return (
      <DropdownField
        attribute={attribute}
        selectedValue={selectedValue}
        error={error}
        onChange={onChange}
      />
    );
  }

  // Free text
  return (
    <TextInputField
      attribute={attribute}
      selectedValue={selectedValue}
      error={error}
      onChange={onChange}
    />
  );
}

// ── DropdownField ──────────────────────────────────────────────────

function DropdownField({
  attribute,
  selectedValue,
  error,
  onChange,
}: {
  attribute: TikTokAttribute;
  selectedValue: { id?: string; name?: string } | undefined;
  error: string | undefined;
  onChange: (val: { id?: string; name?: string } | null) => void;
}) {
  return (
    <div className="px-4 py-4">
      <div className="flex items-center gap-1 mb-2">
        <span className="text-sm font-medium">{attribute.name}</span>
        {attribute.is_required && (
          <span className="text-red-500 text-sm">*</span>
        )}
      </div>
      <select
        className={`w-full rounded-sm border px-3 py-2 text-sm bg-background outline-none focus:ring-2 focus:ring-primary/40 ${
          error ? "border-red-400" : "border-border"
        }`}
        value={selectedValue?.id ?? ""}
        onChange={(e) => {
          const id = e.target.value;
          if (!id) {
            onChange(null);
          } else {
            const opt = attribute.values.find((v) => v.id === id);
            onChange(opt ? { id: opt.id } : null);
          }
        }}
      >
        <option value="">Select {attribute.name}...</option>
        {attribute.values.map((opt) => (
          <option key={opt.id} value={opt.id}>
            {opt.name}
          </option>
        ))}
      </select>
      {error && <p className="text-xs text-red-500 mt-1">{error}</p>}
    </div>
  );
}

// ── ComboboxField (select or type custom) ──────────────────────────

function ComboboxField({
  attribute,
  selectedValue,
  error,
  onChange,
}: {
  attribute: TikTokAttribute;
  selectedValue: { id?: string; name?: string } | undefined;
  error: string | undefined;
  onChange: (val: { id?: string; name?: string } | null) => void;
}) {
  const [isCustom, setIsCustom] = useState(
    // Start in custom mode if current value has name but no matching id
    !!selectedValue?.name && !selectedValue?.id,
  );
  const [customText, setCustomText] = useState(selectedValue?.name ?? "");
  const debounceRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  const handleCustomChange = useCallback(
    (text: string) => {
      setCustomText(text);
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => {
        if (text.trim()) {
          onChange({ name: text.trim() });
        } else {
          onChange(null);
        }
      }, 300);
    },
    [onChange],
  );

  // Cleanup debounce timer
  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  return (
    <div className="px-4 py-4">
      <div className="flex items-center gap-1 mb-2">
        <span className="text-sm font-medium">{attribute.name}</span>
        {attribute.is_required && (
          <span className="text-red-500 text-sm">*</span>
        )}
      </div>

      {!isCustom ? (
        <>
          <select
            className={`w-full rounded-sm border px-3 py-2 text-sm bg-background outline-none focus:ring-2 focus:ring-primary/40 ${
              error ? "border-red-400" : "border-border"
            }`}
            value={selectedValue?.id ?? ""}
            onChange={(e) => {
              const id = e.target.value;
              if (!id) {
                onChange(null);
              } else {
                const opt = attribute.values.find((v) => v.id === id);
                onChange(opt ? { id: opt.id } : null);
              }
            }}
          >
            <option value="">Select {attribute.name}...</option>
            {attribute.values.map((opt) => (
              <option key={opt.id} value={opt.id}>
                {opt.name}
              </option>
            ))}
          </select>
          <button
            type="button"
            className="text-xs text-primary mt-1.5 hover:underline"
            onClick={() => setIsCustom(true)}
          >
            Or enter custom value
          </button>
        </>
      ) : (
        <>
          <input
            type="text"
            className={`w-full rounded-sm border px-3 py-2 text-sm bg-background outline-none focus:ring-2 focus:ring-primary/40 ${
              error ? "border-red-400" : "border-border"
            }`}
            value={customText}
            onChange={(e) => handleCustomChange(e.target.value)}
            placeholder={`Custom ${attribute.name}...`}
          />
          <button
            type="button"
            className="text-xs text-primary mt-1.5 hover:underline"
            onClick={() => {
              setIsCustom(false);
              setCustomText("");
              onChange(null);
            }}
          >
            Select from list instead
          </button>
        </>
      )}
      {error && <p className="text-xs text-red-500 mt-1">{error}</p>}
    </div>
  );
}

// ── TextInputField ─────────────────────────────────────────────────

function TextInputField({
  attribute,
  selectedValue,
  error,
  onChange,
}: {
  attribute: TikTokAttribute;
  selectedValue: { id?: string; name?: string } | undefined;
  error: string | undefined;
  onChange: (val: { id?: string; name?: string } | null) => void;
}) {
  const [text, setText] = useState(selectedValue?.name ?? "");

  const handleBlur = useCallback(() => {
    if (text.trim()) {
      onChange({ name: text.trim() });
    } else {
      onChange(null);
    }
  }, [text, onChange]);

  return (
    <div className="px-4 py-4">
      <div className="flex items-center gap-1 mb-2">
        <span className="text-sm font-medium">{attribute.name}</span>
        {attribute.is_required && (
          <span className="text-red-500 text-sm">*</span>
        )}
      </div>
      <input
        type="text"
        className={`w-full rounded-sm border px-3 py-2 text-sm bg-background outline-none focus:ring-2 focus:ring-primary/40 ${
          error ? "border-red-400" : "border-border"
        }`}
        value={text}
        onChange={(e) => setText(e.target.value)}
        onBlur={handleBlur}
        placeholder={`Enter ${attribute.name}...`}
      />
      {error && <p className="text-xs text-red-500 mt-1">{error}</p>}
    </div>
  );
}

